import Add16 from '@carbon/icons-react/es/add/16';
import { Upload } from 'antd';
import { RcFile } from 'antd/es/upload/interface';
import { isArray } from 'lodash';
import React, { FC, useEffect, useRef, useState } from 'react';
import {
  Controller,
  FieldValues,
  RefCallBack,
  UseFormSetValue,
} from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FileUploadStatus } from 'api/attachments';
import { AttachmentType } from 'api/attachments/types';
import {
  FormAnswerImage,
  FormInstanceApi,
  UploadFormAnswerImage,
} from 'api/form';
import { Button } from 'components/atoms/button';
import { Empty as EmptyAtom } from 'components/atoms/empty';
import { AttachmentImage } from 'components/molecules/attachment-image';
import { toastService } from 'components/molecules/toast';
import { AttachmentFieldUploadData } from 'components/organisms/forms/utils';
import { HelperText01, Text03 } from 'components/styles';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { handleDownload } from 'utils/attachment';
import styles from './attachment-field.module.css';
import {
  FieldsErrorRenderer,
  FormFieldProps,
  FormItemDesc,
  FormItemLabel,
  FormItemWrapper,
} from './form-elements';

const { Dragger } = Upload;

export const AttachmentsEmpty = styled(EmptyAtom)`
  flex-direction: column;
  justify-content: center;
  margin-top: var(--spacing-05);
`;
const Description = styled.div`
  ${HelperText01};
  ${Text03};
`;
const AttachmentGalleryList = styled.div`
  display: flex;
  flex-flow: row wrap;
  padding: var(--spacing-05) 0 0;
  gap: var(--spacing-05);
  margin-bottom: var(--spacing-05);
`;

export const convertFileAttachmentToUploadFileWithAttachment = (
  data: AttachmentFieldUploadData[],
): UploadFormAnswerImage[] => {
  return data.map((d) => {
    if (!d.uid && d.attachmentId) {
      return {
        ...d,
        uid: `${d.attachmentId}-${d.createdAt}`,
        size: d.sizeInBytes,
        type: 'TYPE',
        attachment: d,
        name: d.name,
        status: FileUploadStatus.Done,
        thumbUrl: d?.thumbLocation || d?.location,
        url: d?.location,
      };
    }

    return d;
  });
};

const AddButton = ({ disabled }: { disabled: boolean }) => (
  <Button type="link" size="small" icon={<Add16 />} disabled={disabled}>
    <FormattedMessage id="attachment.add" />
  </Button>
);

const AttachmentFieldRenderer = ({
  questionId,
  disabled,
  ref,
  setValue,
  value,
}: {
  questionId: string;
  disabled: boolean;
  ref: RefCallBack;
  setValue?: UseFormSetValue<FieldValues>;
  value: FormAnswerImage[];
}) => {
  const translate = useTranslate();
  const [filesList, setFilesList] = useState<UploadFormAnswerImage[]>([]);
  const latestFileList = useRef<UploadFormAnswerImage[]>(filesList);
  const stringifyData = JSON.stringify(value || []);
  const downloadAttachmentErrorText = useTranslation(
    'attachments.download.error',
  );

  useEffect(() => {
    latestFileList.current = filesList;
  }, [filesList]);

  useEffect(() => {
    setFilesList(
      convertFileAttachmentToUploadFileWithAttachment(
        JSON.parse(stringifyData),
      ),
    );
  }, [stringifyData]);

  const getUpdatedFileList = (
    file: UploadFormAnswerImage,
    fileList: UploadFormAnswerImage[],
    attachment?: FormAnswerImage,
    status?: FileUploadStatus,
  ): Array<UploadFormAnswerImage> => {
    const updatedFile: UploadFormAnswerImage = {
      ...file,
      attachment,
      status,
      thumbUrl: attachment?.location,
      url: attachment?.location,
    };

    return fileList.map((f) => (f.uid === updatedFile.uid ? updatedFile : f));
  };
  const updateFile = ({
    attachment,
    file,
    status,
  }: {
    attachment?: FormAnswerImage;
    file: RcFile;
    status: FileUploadStatus;
  }) => {
    const latestFiles = latestFileList.current;
    const fileToUpdate = latestFiles.find((f) => f.uid === file.uid);

    if (!fileToUpdate) {
      return;
    }

    const result = getUpdatedFileList(
      fileToUpdate,
      latestFiles,
      attachment,
      status,
    );

    if (result) {
      setFilesList(result);

      setValue?.(questionId, result);
    }
  };
  const getTaskInfo = () => {
    try {
      // example url : https://deepakkumar.nitro.run/projects/169497/plan#task_drawer=169497.4446537&task_drawer_tab=conversations
      // after split : ['https:', '', 'deepakkumar.nitro.run', 'projects', '169497', 'plan#task_drawer=169497.4446537&task_drawer_tab=conversations']
      const taskInfo = window.location.href.split('/')[5]; // --> plan#task_drawer=169497.4446537&task_drawer_tab=conversations
      const path = window.location.pathname.split('/'); // --> to retrieve projectId --> 169497

      return {
        projectId: parseInt(path[3], 10),
        taskId: parseInt(taskInfo.split('.')[1].split('&')[0], 10), // plan#task_drawer=169497.4446537&task_drawer_tab=conversations --> 4446537&task_drawer=conversations --> 4446537
      };
    } catch {
      return undefined;
    }
  };
  const customUploadRequest = (args) => {
    const { file } = args;
    const taskInfo = getTaskInfo();

    FormInstanceApi.uploadFormAnswerImage({
      file,
      attachment: {
        name: file.name,
        context: 'FORM',
        ...(taskInfo != null
          ? {
              sourceType: AttachmentType.Task,
              projectId: taskInfo.projectId,
              sourceId: taskInfo.taskId,
            }
          : {}),
      },
    })
      .then((res) => {
        updateFile({
          attachment: res,
          file,
          status: FileUploadStatus.Done,
        });
      })
      .catch(({ response = {} }) => {
        const errorData = response.data || {};
        const [error = {}] = errorData.errors || [];

        if (error.code === 'ATTACHMENT_SIZE_EXCEEDED') {
          toastService({
            title: translate('attachment.error.size.title'),
            message: translate('attachment.add.description'),
          }).error();
        }
        updateFile({
          attachment: undefined,
          file,
          status: FileUploadStatus.Error,
        });
      });
  };
  const handleOnRemove = (file: UploadFormAnswerImage) => {
    const index = filesList.indexOf(file);
    const newFileList = [...filesList];

    newFileList.splice(index, 1);

    setFilesList(newFileList);
    setValue?.(questionId, newFileList);

    return true;
  };

  const handleOnChange = (args) => {
    const { file } = args;
    const updatedFilesList = [...latestFileList.current, file];

    setFilesList(updatedFilesList);
    setValue?.(questionId, updatedFilesList);
  };

  return (
    <div ref={ref}>
      <Dragger
        className={styles.dragger}
        customRequest={customUploadRequest}
        onChange={handleOnChange}
        multiple
        disabled={disabled}
        showUploadList={false}
        openFileDialogOnClick={!filesList.length}
      >
        <div className={styles.content_wrapper}>
          {filesList.length ? (
            <AttachmentGalleryList>
              {filesList.map((file) => {
                return (
                  <AttachmentImage
                    imageThumbnailClassName={styles.image_item_wrapper}
                    originFileObj={file.originFileObj}
                    fileUid={file.uid}
                    fileUploadStatus={file.status}
                    name={file.name}
                    attachmentId={file.attachment?.attachmentId}
                    thumbUrl={file.thumbUrl}
                    url={file.url}
                    imageWidth={136}
                    imageHeight={80}
                    onRemove={() => handleOnRemove(file)}
                    disableRemoveFile={disabled}
                    large
                    showCustomPreview
                    onDownload={() =>
                      handleDownload(
                        file.attachment?.attachmentId,
                        downloadAttachmentErrorText,
                      )
                    }
                    isBasicPreview
                  />
                );
              })}
            </AttachmentGalleryList>
          ) : (
            <AttachmentsEmpty>
              <AddButton disabled={disabled} />
              <Description>
                <FormattedMessage id="attachment.add.description" />
              </Description>
            </AttachmentsEmpty>
          )}
        </div>
      </Dragger>
      {filesList.length > 0 && (
        <Upload
          customRequest={customUploadRequest}
          onChange={handleOnChange}
          multiple
          disabled={disabled}
          showUploadList={false}
        >
          <AddButton disabled={disabled} />
        </Upload>
      )}
    </div>
  );
};

export const AttachmentField: FC<FormFieldProps> = ({
  field,
  errors,
  setValue,
  control,
  disabled,
}) => {
  return (
    <FormItemWrapper>
      <FormItemLabel label={field.questionText} required={field.required} />
      <FormItemDesc label={field.description} />
      <Controller
        control={control}
        rules={{
          validate: {
            filesRequired: (value) => {
              const attachmentFieldValue = value
                ? (value as UploadFormAnswerImage[])
                : [];

              if (field.required) {
                if (
                  attachmentFieldValue &&
                  isArray(attachmentFieldValue) &&
                  attachmentFieldValue.length === 0
                ) {
                  return false;
                }
              }

              return true;
            },
          },
        }}
        name={field.questionId.toString()}
        shouldUnregister
        render={(props) => (
          <AttachmentFieldRenderer
            ref={props.field.ref}
            value={props.field.value}
            questionId={field.questionId.toString()}
            disabled={disabled}
            setValue={setValue}
          />
        )}
      />
      <FieldsErrorRenderer errors={errors} questionId={field.questionId} />
    </FormItemWrapper>
  );
};
